/**
 * IXVerify v2 — Subjects page (ixv2 WP-2.3; plan §3.4).
 *
 * The subject roster for the current site: list/search/filter, manual add,
 * CSV import (preview → commit), §3.4 pending-confirmation chips, and
 * source badges (crio | manual | csv).
 *
 * Backend: the 5 subject routes are live (WP-2.1). The studies list that
 * feeds the study selector is a 501 stub until WP-3.1 — the roster still
 * works unfiltered; add/import fall back to a clean "study list not yet
 * available" notice (subjects require a `site_study_id`).
 *
 * R3: DOB is PHI. It renders in the roster (the admin confirmation flow in
 * §3.4 verifies the DOB entry) and is never logged or echoed elsewhere.
 * R5: the pending chip reflects server state; confirmation is an explicit,
 * admin-initiated action — the UI never auto-confirms.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Plus, Upload, ChevronLeft, ChevronRight, AlertTriangle, ShieldCheck } from 'lucide-react';
import {
  PageHeader,
  RefreshButton,
  SearchInput,
  FilterSelect,
  Modal,
  ConfirmModal,
  EmptyState,
  useToast,
  primaryBtnCls,
  secondaryBtnCls,
  outlineBtnCls,
  fieldCls,
  chipCls,
} from '../../ui';
import { useAuth } from '../../contexts/AuthContext';
import {
  listSubjects,
  createSubject,
  confirmSubject,
  importSubjectsPreview,
  importSubjectsCommit,
  listStudies,
  is501,
  isEncryptionUnavailable,
  errorMessage,
  SUBJECT_STATUSES,
  IxvSubject,
  IxvStudy,
  ImportPreview,
  ImportCommitResult,
} from '../lib/api';
import { DOB_FORMAT_EXAMPLE, formatDob, parseDob } from '../lib/dob';
import { SourceBadge, PendingConfirmationChip, SubjectStatusPill } from '../components/shared/badges';

const PAGE_SIZE = 50;

const label = 'mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

function DobCell({ subject }: { subject: IxvSubject }) {
  if (subject.dob_unreadable) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        Unreadable
      </span>
    );
  }
  if (!subject.dob) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  return <span className="tabular-nums">{formatDob(subject.dob)}</span>;
}

export default function Subjects() {
  const { currentSite, isAdmin, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const canConfirm = isAdmin || isSuperAdmin;

  // --- Studies (for the study selector; 501-tolerant) ---
  const [studies, setStudies] = useState<IxvStudy[]>([]);
  const [studiesStubbed, setStudiesStubbed] = useState(false);
  const [studiesLoaded, setStudiesLoaded] = useState(false);

  // --- Roster list state ---
  const [subjects, setSubjects] = useState<IxvSubject[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [encryptionDown, setEncryptionDown] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [studyFilter, setStudyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [offset, setOffset] = useState(0);

  // --- Add-subject modal ---
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ site_study_id: '', subject_number: '', initials: '', dob: '', status: '' });
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);
  // DOB is typed as 01Dec2020 and sent as ISO (lib/dob — the one format).
  const addDobIso = parseDob(addForm.dob);
  const addDobInvalid = addForm.dob.trim() !== '' && !addDobIso;

  // --- Import wizard ---
  const [importOpen, setImportOpen] = useState(false);
  const [importStudyId, setImportStudyId] = useState('');
  const [importCsv, setImportCsv] = useState('');
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [importResult, setImportResult] = useState<ImportCommitResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importBusy, setImportBusy] = useState(false);

  // --- Confirm-DOB dialog ---
  const [confirmTarget, setConfirmTarget] = useState<IxvSubject | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Studies for the selector — a 501 here is expected until WP-3.1.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listStudies();
        if (!cancelled) {
          setStudies(list);
          setStudiesStubbed(false);
        }
      } catch (err) {
        if (!cancelled) {
          setStudies([]);
          setStudiesStubbed(is501(err));
        }
      } finally {
        if (!cancelled) setStudiesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentSite?.id]);

  const fetchSubjects = useCallback(async () => {
    setLoading(true);
    setListError(null);
    setEncryptionDown(false);
    try {
      const result = await listSubjects({
        study: studyFilter || undefined,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setSubjects(result.subjects || []);
    } catch (err) {
      if (isEncryptionUnavailable(err)) {
        setEncryptionDown(true);
      } else {
        setListError(errorMessage(err, 'Failed to load the subject roster'));
      }
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  }, [studyFilter, debouncedSearch, statusFilter, offset]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  // Reset paging when a filter changes.
  useEffect(() => {
    setOffset(0);
  }, [studyFilter, debouncedSearch, statusFilter]);

  const visibleSubjects = useMemo(
    () => (pendingOnly ? subjects.filter((s) => s.pending_confirmation) : subjects),
    [subjects, pendingOnly]
  );
  const pendingCount = useMemo(() => subjects.filter((s) => s.pending_confirmation).length, [subjects]);

  // Pilot r2: archived studies leave the filter + add/import selectors, but
  // `studyName` keeps the FULL list so rows in archived studies still label.
  const studyOptions = useMemo(
    () => studies.filter((s) => !s.archived_at).map((s) => ({ value: s.id, label: s.protocol_id || s.id })),
    [studies]
  );
  const studyName = useCallback(
    (id: string) => studies.find((s) => s.id === id)?.protocol_id ?? null,
    [studies]
  );

  // --- Handlers ---

  const handleAdd = async () => {
    if (!addForm.site_study_id) {
      setAddError('Select a study');
      return;
    }
    if (!addForm.subject_number.trim()) {
      setAddError('Subject number is required');
      return;
    }
    if (addDobInvalid) {
      setAddError(`Enter the date of birth as ${DOB_FORMAT_EXAMPLE}`);
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      await createSubject({
        site_study_id: addForm.site_study_id,
        subject_number: addForm.subject_number.trim(),
        initials: addForm.initials.trim() || undefined,
        dob: addDobIso || undefined,
        status: addForm.status || undefined,
      });
      setAddOpen(false);
      setAddForm({ site_study_id: '', subject_number: '', initials: '', dob: '', status: '' });
      toast('Subject added');
      fetchSubjects();
    } catch (err) {
      setAddError(errorMessage(err, 'Failed to add subject'));
    } finally {
      setAddSaving(false);
    }
  };

  const handleImportPreview = async () => {
    if (!importStudyId) {
      setImportError('Select a study');
      return;
    }
    if (!importCsv.trim()) {
      setImportError('Paste or choose a CSV file first');
      return;
    }
    setImportBusy(true);
    setImportError(null);
    try {
      const preview = await importSubjectsPreview(importStudyId, importCsv);
      setImportPreview(preview);
    } catch (err) {
      setImportError(errorMessage(err, 'Failed to preview the import'));
    } finally {
      setImportBusy(false);
    }
  };

  const handleImportCommit = async () => {
    setImportBusy(true);
    setImportError(null);
    try {
      const result = await importSubjectsCommit(importStudyId, importCsv);
      setImportResult(result);
      toast(`Import complete: ${result.created} created, ${result.updated} updated`);
      fetchSubjects();
    } catch (err) {
      setImportError(errorMessage(err, 'Failed to import subjects'));
    } finally {
      setImportBusy(false);
    }
  };

  const resetImport = () => {
    setImportOpen(false);
    setImportStudyId('');
    setImportCsv('');
    setImportPreview(null);
    setImportResult(null);
    setImportError(null);
  };

  const handleCsvFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImportCsv(String(reader.result ?? ''));
    reader.readAsText(file);
  };

  const handleConfirm = async () => {
    if (!confirmTarget) return;
    setConfirmBusy(true);
    try {
      await confirmSubject(confirmTarget.id);
      toast(`Subject ${confirmTarget.subject_number} confirmed`);
      setConfirmTarget(null);
      fetchSubjects();
    } catch (err) {
      toast(errorMessage(err, 'Failed to confirm subject'), { type: 'error' });
    } finally {
      setConfirmBusy(false);
    }
  };

  // The forms need a study to target; until WP-3.1 serves the study list the
  // add/import flows show a quiet notice instead.
  const studiesUnavailable = studiesLoaded && studiesStubbed;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Subjects"
        icon={Users}
        count={loading ? null : visibleSubjects.length}
        subtitle="Subject roster for dispensing verification"
        actions={
          <>
            <RefreshButton onClick={fetchSubjects} loading={loading} />
            <button type="button" className={secondaryBtnCls} onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              Import CSV
            </button>
            <button type="button" className={primaryBtnCls} onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add subject
            </button>
          </>
        }
      />

      {/* Filter bar — chromeless (page chrome, not content) */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search subject # or initials…"
          label="Search subjects"
          className="flex-1 basis-56"
        />
        {studies.length > 0 && (
          <FilterSelect
            value={studyFilter}
            onChange={setStudyFilter}
            label="Filter by study"
            auto
            options={[{ value: '', label: 'All studies' }, ...studyOptions]}
          />
        )}
        <FilterSelect
          value={statusFilter}
          onChange={setStatusFilter}
          label="Filter by status"
          auto
          options={[
            { value: '', label: 'All statuses' },
            ...SUBJECT_STATUSES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })),
          ]}
        />
        <button
          type="button"
          className={chipCls(pendingOnly)}
          aria-pressed={pendingOnly}
          onClick={() => setPendingOnly((v) => !v)}
        >
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Pending confirmation{pendingCount > 0 ? ` (${pendingCount})` : ''}
        </button>
      </div>

      {/* Body */}
      {encryptionDown ? (
        <EmptyState
          variant="card"
          icon={AlertTriangle}
          title="Roster temporarily unavailable"
          hint="Subject data is unavailable while the service finishes provisioning. Please try again shortly."
        />
      ) : listError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-sm text-red-700 dark:text-red-400">{listError}</p>
          <button type="button" className={`${outlineBtnCls()} mt-3`} onClick={fetchSubjects}>
            Try again
          </button>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-12" role="status" aria-label="Loading subjects">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
        </div>
      ) : visibleSubjects.length === 0 ? (
        <EmptyState
          variant="card"
          icon={Users}
          title={pendingOnly ? 'No subjects pending confirmation' : 'No subjects yet'}
          hint={
            pendingOnly
              ? 'Every manual and CSV subject on this page has a confirmed date of birth.'
              : 'Subjects sync automatically from CRIO where connected; add them manually or import a CSV for other studies.'
          }
          action={
            !pendingOnly && (
              <button type="button" className={primaryBtnCls} onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add subject
              </button>
            )
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 sm:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Subject #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Initials</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Date of birth</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Confirmation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {visibleSubjects.map((s) => (
                    <tr key={s.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {s.subject_number}
                        {studyName(s.site_study_id) && (
                          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                            {studyName(s.site_study_id)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{s.initials || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100"><DobCell subject={s} /></td>
                      <td className="px-4 py-3 text-sm">
                        {s.status ? <SubjectStatusPill status={s.status} /> : <span className="text-gray-400 dark:text-gray-500">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm"><SourceBadge source={s.source} /></td>
                      <td className="px-4 py-3 text-sm">
                        {s.pending_confirmation ? (
                          <span className="inline-flex items-center gap-2">
                            <PendingConfirmationChip />
                            {canConfirm && (
                              <button
                                type="button"
                                className={outlineBtnCls()}
                                onClick={() => setConfirmTarget(s)}
                              >
                                Confirm
                              </button>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">Confirmed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards (390px-first) */}
          <ul className="space-y-2 sm:hidden">
            {visibleSubjects.map((s) => (
              <li key={s.id} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {s.subject_number}
                      {s.initials && <span className="ml-2 font-normal text-gray-500 dark:text-gray-400">{s.initials}</span>}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      DOB: <DobCell subject={s} />
                    </p>
                  </div>
                  <SourceBadge source={s.source} />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {s.status && <SubjectStatusPill status={s.status} />}
                  {s.pending_confirmation && <PendingConfirmationChip />}
                </div>
                {s.pending_confirmation && canConfirm && (
                  <button
                    type="button"
                    className={`${outlineBtnCls()} mt-3 w-full justify-center`}
                    onClick={() => setConfirmTarget(s)}
                  >
                    Confirm date of birth
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* Pager */}
          {(offset > 0 || subjects.length === PAGE_SIZE) && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Showing {offset + 1}–{offset + subjects.length}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={outlineBtnCls()}
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  Previous
                </button>
                <button
                  type="button"
                  className={outlineBtnCls()}
                  disabled={subjects.length < PAGE_SIZE || loading}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---- Add subject ---- */}
      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Add subject" maxWidth="max-w-md">
        {studiesUnavailable ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Adding subjects needs the study list, which isn't available yet in IXVerify. It arrives
            with study management — check back soon.
          </p>
        ) : (
          <div className="space-y-4">
            {addError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-400">{addError}</p>
              </div>
            )}
            <div>
              <p className={label}>Study *</p>
              <FilterSelect
                value={addForm.site_study_id}
                onChange={(v) => setAddForm((f) => ({ ...f, site_study_id: v }))}
                label="Study"
                options={[{ value: '', label: 'Select a study…' }, ...studyOptions]}
              />
            </div>
            <div>
              <label htmlFor="ixv-add-number" className={label}>Subject number *</label>
              <input
                id="ixv-add-number"
                className={`${fieldCls} w-full`}
                value={addForm.subject_number}
                onChange={(e) => setAddForm((f) => ({ ...f, subject_number: e.target.value }))}
                placeholder="As printed on IXRS documents"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ixv-add-initials" className={label}>Initials</label>
                <input
                  id="ixv-add-initials"
                  className={`${fieldCls} w-full uppercase`}
                  value={addForm.initials}
                  onChange={(e) => setAddForm((f) => ({ ...f, initials: e.target.value }))}
                  maxLength={4}
                />
              </div>
              <div>
                <label htmlFor="ixv-add-dob" className={label}>Date of birth</label>
                <input
                  id="ixv-add-dob"
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={DOB_FORMAT_EXAMPLE}
                  aria-describedby="ixv-add-dob-hint"
                  aria-invalid={addDobInvalid || undefined}
                  className={`${fieldCls} w-full`}
                  value={addForm.dob}
                  onChange={(e) => setAddForm((f) => ({ ...f, dob: e.target.value }))}
                />
                <p
                  id="ixv-add-dob-hint"
                  className={`mt-1 text-xs ${addDobInvalid ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}
                >
                  {addDobIso ? `Reads as ${formatDob(addDobIso)}` : addDobInvalid ? `Type it as ${DOB_FORMAT_EXAMPLE}` : `Like ${DOB_FORMAT_EXAMPLE}`}
                </p>
              </div>
            </div>
            <div>
              <p className={label}>Status</p>
              <FilterSelect
                value={addForm.status}
                onChange={(v) => setAddForm((f) => ({ ...f, status: v }))}
                label="Status"
                options={[
                  { value: '', label: 'Not set' },
                  ...SUBJECT_STATUSES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })),
                ]}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Manually added subjects carry a "Pending confirmation" chip until an administrator
              verifies the date-of-birth entry.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className={secondaryBtnCls} onClick={() => setAddOpen(false)}>
                Cancel
              </button>
              <button type="button" className={primaryBtnCls} onClick={handleAdd} disabled={addSaving}>
                {addSaving ? 'Adding…' : 'Add subject'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ---- Import wizard: input → preview → commit → result ---- */}
      <Modal isOpen={importOpen} onClose={resetImport} title="Import subjects from CSV">
        {studiesUnavailable ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            CSV import needs the study list, which isn't available yet in IXVerify. It arrives with
            study management — check back soon.
          </p>
        ) : importResult ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">Import complete</p>
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                {importResult.created} created · {importResult.updated} updated · {importResult.unchanged} unchanged
                {importResult.errors.length > 0 && ` · ${importResult.errors.length} rows skipped`}
              </p>
            </div>
            {importResult.errors.length > 0 && (
              <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-red-700 dark:text-red-400">
                {importResult.errors.map((e) => (
                  <li key={e.row}>Line {e.row}: {e.error}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <button type="button" className={primaryBtnCls} onClick={resetImport}>Done</button>
            </div>
          </div>
        ) : importPreview ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Preview only — nothing has been written yet. Review the changes, then import.
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-gray-200 p-3 text-center dark:border-gray-700">
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{importPreview.to_create.length}</p>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">New</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 text-center dark:border-gray-700">
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{importPreview.to_update.length}</p>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Updated</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 text-center dark:border-gray-700">
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{importPreview.unchanged}</p>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Unchanged</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 text-center dark:border-gray-700">
                <p className={`text-lg font-bold ${importPreview.errors.length ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {importPreview.errors.length}
                </p>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Errors</p>
              </div>
            </div>
            {importPreview.to_create.length > 0 && (
              <div>
                <p className={label}>Will be created</p>
                <div className="flex flex-wrap gap-1.5">
                  {importPreview.to_create.map((r) => (
                    <span key={r.row} className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                      {r.subject_number}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {importPreview.to_update.length > 0 && (
              <div>
                <p className={label}>Will be updated</p>
                <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  {importPreview.to_update.map((r) => (
                    <li key={r.row}>
                      <span className="font-medium">{r.subject_number}</span>
                      <span className="text-gray-500 dark:text-gray-400"> — {r.changes.join(', ')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {importPreview.errors.length > 0 && (
              <div>
                <p className={label}>Rows with errors (skipped)</p>
                <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-red-700 dark:text-red-400">
                  {importPreview.errors.map((e) => (
                    <li key={e.row}>Line {e.row}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            {importError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-400">{importError}</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className={secondaryBtnCls} onClick={() => setImportPreview(null)} disabled={importBusy}>
                Back
              </button>
              <button
                type="button"
                className={primaryBtnCls}
                onClick={handleImportCommit}
                disabled={importBusy || importPreview.to_create.length + importPreview.to_update.length === 0}
              >
                {importBusy
                  ? 'Importing…'
                  : `Import ${importPreview.to_create.length + importPreview.to_update.length} rows`}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {importError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-400">{importError}</p>
              </div>
            )}
            <div>
              <p className={label}>Study *</p>
              <FilterSelect
                value={importStudyId}
                onChange={setImportStudyId}
                label="Study"
                options={[{ value: '', label: 'Select a study…' }, ...studyOptions]}
              />
            </div>
            <div>
              <label htmlFor="ixv-import-file" className={label}>CSV file</label>
              <input
                id="ixv-import-file"
                type="file"
                accept=".csv,text/csv"
                className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-600 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-primary-700 dark:text-gray-300"
                onChange={(e) => handleCsvFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div>
              <label htmlFor="ixv-import-csv" className={label}>Or paste CSV</label>
              <textarea
                id="ixv-import-csv"
                className={`${fieldCls} min-h-[8rem] w-full font-mono text-xs`}
                value={importCsv}
                onChange={(e) => setImportCsv(e.target.value)}
                placeholder={'subject number,initials,dob,status'}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Recognized columns: subject number, initials, date of birth, status. Blank cells never
                clear existing values. You'll review a preview before anything is written.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className={secondaryBtnCls} onClick={resetImport}>Cancel</button>
              <button type="button" className={primaryBtnCls} onClick={handleImportPreview} disabled={importBusy}>
                {importBusy ? 'Previewing…' : 'Preview import'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ---- Explicit admin DOB confirmation (R5: never automatic) ---- */}
      <ConfirmModal
        isOpen={confirmTarget != null}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleConfirm}
        loading={confirmBusy}
        title="Confirm date of birth"
        confirmLabel="Confirm entry"
        message={
          confirmTarget && (
            <>
              Confirm that the recorded date of birth for subject{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">{confirmTarget.subject_number}</span>
              {confirmTarget.dob && (
                <>
                  {' '}(<span className="tabular-nums">{formatDob(confirmTarget.dob)}</span>)
                </>
              )}{' '}
              is correct. Your confirmation is recorded.
            </>
          )
        }
      />
    </div>
  );
}
